/**
 * Hero slideshow analytics
 * ------------------------
 * Reads the data-hero-* attributes rendered by sections/section-slideshow.liquid
 * and pushes two events to window.dataLayer for Google Tag Manager -> GA4:
 *
 *   hero_slide_view   – fired once per slide per page load, the first time that
 *                       slide is the selected slide AND the slideshow is at least
 *                       50% in the viewport AND the tab is visible.
 *   hero_slide_click  – fired when a link inside a slide is clicked (primary /
 *                       secondary button or the full-image link).
 *
 * Both events carry the same slide parameters so GA4 can compute
 * click-through rate per creative:
 *   hero_campaign, hero_slide_id, hero_position, hero_slides_total,
 *   hero_headline, hero_subheading, hero_image, hero_image_mobile, hero_location
 * Click events add: hero_cta, hero_cta_text, hero_cta_url
 *
 * The same payload is also published via Shopify.analytics.publish so a Shopify
 * custom pixel can forward it to non-GTM destinations if ever needed.
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return; // no tracking in the theme editor

  var SLIDESHOW = '[data-hero-slideshow]';
  var SLIDE = '[data-hero-slide]';
  var SELECTED_CLASS = 'is-selected';
  var VIEW_EVENT = 'hero_slide_view';
  var CLICK_EVENT = 'hero_slide_click';
  var VISIBLE_RATIO = 0.5;

  window.dataLayer = window.dataLayer || [];

  function push(payload) {
    window.dataLayer.push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(payload.event, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  function slideMeta(slide, slideshow) {
    var d = slide.dataset;
    return {
      hero_campaign: d.heroCampaign || '',
      hero_slide_id: d.heroSlideId || '',
      hero_position: parseInt(d.heroPosition, 10) || 0,
      hero_slides_total: parseInt(slideshow.dataset.slidesCount, 10) || 0,
      hero_headline: d.heroHeadline || '',
      hero_subheading: d.heroSubheading || '',
      hero_image: d.heroImage || '',
      hero_image_mobile: d.heroImageMobile || '',
      hero_location: slideshow.dataset.heroLocation || '',
      hero_viewport: window.innerWidth < 768 ? 'mobile' : 'desktop'
    };
  }

  function initSlideshow(slideshow) {
    if (slideshow.__heroAnalytics) return;
    slideshow.__heroAnalytics = true;

    var slides = Array.prototype.slice.call(slideshow.querySelectorAll(SLIDE));
    if (!slides.length) return;

    var viewed = {};
    var inViewport = false;

    function selectedSlide() {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].classList.contains(SELECTED_CLASS)) return slides[i];
      }
      return slides.length === 1 ? slides[0] : null;
    }

    function maybeTrackView() {
      if (!inViewport || document.visibilityState !== 'visible') return;
      var slide = selectedSlide();
      if (!slide) return;
      var id = slide.dataset.heroSlideId || String(slides.indexOf(slide));
      if (viewed[id]) return;
      viewed[id] = true;
      var payload = slideMeta(slide, slideshow);
      payload.event = VIEW_EVENT;
      push(payload);
    }

    // 1. Slide changes: Flickity toggles .is-selected on the active cell.
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(maybeTrackView);
      slides.forEach(function (slide) {
        mo.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });
    }

    // 2. Viewport visibility: only count a view when the hero is actually on screen.
    //    "On screen" = at least half the hero is visible, OR the hero covers at
    //    least half the viewport (tall heroes on short screens never reach 50%).
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        var rootHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
        inViewport = entry.isIntersecting && (
          entry.intersectionRatio >= VISIBLE_RATIO ||
          (rootHeight > 0 && entry.intersectionRect.height >= rootHeight * VISIBLE_RATIO)
        );
        maybeTrackView();
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
      io.observe(slideshow);
    } else {
      inViewport = true;
      maybeTrackView();
    }

    // 3. Tab visibility: a slide selected while the tab is hidden counts when the user returns.
    document.addEventListener('visibilitychange', maybeTrackView);

    // 4. Clicks on any link inside a slide.
    slideshow.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link || !slideshow.contains(link)) return;
      var slide = link.closest(SLIDE);
      if (!slide) return;

      var payload = slideMeta(slide, slideshow);
      payload.event = CLICK_EVENT;
      payload.hero_cta = link.dataset.heroCta || 'other';
      payload.hero_cta_text = link.dataset.heroCtaText || (link.textContent || '').trim().slice(0, 100);
      payload.hero_cta_url = link.getAttribute('href') || '';
      push(payload);
    }, true);
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(SLIDESHOW), initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-rendered by the theme editor / section rendering API.
  document.addEventListener('shopify:section:load', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll(SLIDESHOW), initSlideshow);
  });
})();
